import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createSynonymAlias,
  deleteTerminologyItem,
  getTerminologyItem,
  listTerminologyItems,
} from "@/api/terminology";
import { useToast } from "@/components/toast";

import { SenseSearch } from "../SenseSearch";
import type { SenseTarget } from "../senseSearchTypes";

export function SynonymPanel() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [picked, setPicked] = useState<SenseTarget | null>(null);
  const [aliasText, setAliasText] = useState("");

  const detail = useQuery({
    queryKey: ["terminology-item", picked?.item_id],
    queryFn: () => getTerminologyItem(picked!.item_id),
    enabled: !!picked,
  });
  const item = detail.data?.ok ? detail.data.data.item : null;
  const canonical = item?.target_text ?? "";
  const targetLang = (item?.target_lang === "en" ? "en" : "ru") as "en" | "ru";

  const existing = useQuery({
    queryKey: ["synonyms", canonical],
    queryFn: () =>
      listTerminologyItems({
        q: canonical,
        item_type: "term_alias",
        status: "approved",
        limit: 20,
      }),
    enabled: !!canonical,
  });
  const synonyms = (existing.data?.ok ? existing.data.data.items : []).filter(
    (it) =>
      (it.target_text ?? "") === canonical &&
      (it.external_ids as Record<string, unknown> | undefined)
        ?.replace_with_canonical !== true,
  );

  function refetch() {
    void qc.invalidateQueries({ queryKey: ["synonyms", canonical] });
  }

  const addMut = useMutation({
    mutationFn: async () => {
      const r = await createSynonymAlias({
        canonical_text: canonical,
        alias_text: aliasText.trim(),
        target_lang: targetLang,
        domain: item?.domain ?? "Term",
        source_key: item?.source_key ?? "",
      });
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      toast("Синоним добавлен", "success");
      setAliasText("");
      refetch();
    },
    onError: (e: Error) => toast(e.message, "error"),
  });
  const delMut = useMutation({
    mutationFn: async (id: string) => {
      const r = await deleteTerminologyItem(id);
      if (!r.ok) throw new Error(r.error);
      return r.data;
    },
    onSuccess: () => {
      toast("Удалено", "success");
      refetch();
    },
    onError: (e: Error) => toast(e.message, "error"),
  });

  return (
    <div>
      <SenseSearch onPick={setPicked} pickedItemId={picked?.item_id} />
      {picked && detail.isLoading && (
        <div className="mt-muted" style={{ marginTop: 12, fontSize: "var(--fs-sm)" }}>
          Загружаю…
        </div>
      )}
      {picked && item && (
        <div style={{ marginTop: 12 }}>
          <div className="mt-muted" style={{ marginBottom: 6 }}>
            Основной перевод: <b>{canonical || "—"}</b>
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              marginBottom: 8,
            }}
          >
            <div className="mt-muted">Уже разрешено (синонимы):</div>
            {existing.isLoading ? (
              <span className="mt-muted" style={{ fontSize: "var(--fs-sm)" }}>
                загружаю…
              </span>
            ) : synonyms.length === 0 ? (
              <span className="mt-muted" style={{ fontSize: "var(--fs-sm)" }}>
                пока ничего
              </span>
            ) : (
              synonyms.map((s) => (
                <div
                  key={s.item_id}
                  className="rf-example"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: 6,
                    fontSize: "var(--fs-sm)",
                  }}
                >
                  <span style={{ flex: 1 }}>«{s.source_text}»</span>
                  <button
                    type="button"
                    className="mt-btn mt-btn--sm mt-btn--danger mt-btn--ghost"
                    onClick={() => delMut.mutate(s.item_id)}
                  >
                    убрать
                  </button>
                </div>
              ))
            )}
          </div>
          <div
            style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}
          >
            <input
              className="mt-input mt-input--sm"
              placeholder="допустимый вариант перевода"
              value={aliasText}
              onChange={(e) => setAliasText(e.target.value)}
              style={{ minWidth: 240 }}
            />
            <button
              type="button"
              className="mt-btn mt-btn--sm mt-btn--primary"
              disabled={
                !aliasText.trim() ||
                !canonical ||
                !item?.source_key ||
                addMut.isPending
              }
              onClick={() => addMut.mutate()}
            >
              Разрешить
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
